import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "qrcode";
import { Phone, Mail, MapPin, Globe, Instagram, Facebook, MessageCircle, Download, QrCode, X } from "lucide-react";
import { useSEO } from "../components/SEO";
import { CARTOES, CARTAO_COMUM } from "../data/cartoes";

const telHref = (t) => `tel:${t.replace(/\s/g, "")}`;
const waHref = (t) => `https://wa.me/${t.replace(/[\s+]/g, "")}`;
const iniciais = (nome) => nome.split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const buildVCard = (slug, c) => {
    const partes = c.nome.split(" ");
    const apelido = partes.slice(1).join(" ");
    return [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `N:${apelido};${partes[0]};;;`,
        `FN:${c.nome}`,
        "ORG:Associação Fados à Mesa",
        `TITLE:${c.cargo}`,
        `TEL;TYPE=CELL:${c.telemovel.replace(/\s/g, "")}`,
        `EMAIL;TYPE=WORK:${CARTAO_COMUM.email}`,
        "ADR;TYPE=WORK:;;Gerliswilstrasse 46;Emmenbrücke;;6020;Suíça",
        `URL:${CARTAO_COMUM.websiteUrl}`,
        `NOTE:${CARTAO_COMUM.fundador}`,
        `X-SOCIALPROFILE;TYPE=instagram:${CARTAO_COMUM.instagram}`,
        "END:VCARD",
    ].join("\r\n");
};

const Linha = ({ href, icon: Icon, texto, testid, externo }) => (
    <a
        href={href}
        {...(externo ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        data-testid={testid}
        className="flex items-center gap-3.5 group"
    >
        <span className="flex items-center justify-center w-9 h-9 shrink-0 border border-[#c9a24b]/30 text-[#c9a24b] group-hover:bg-[#c9a24b] group-hover:text-[#070503] transition-colors duration-300">
            <Icon size={15} strokeWidth={1.6} />
        </span>
        <span className="font-light text-sm text-[#efe6d6]/85 group-hover:text-[#efe6d6] transition-colors duration-300 text-left break-all">
            {texto}
        </span>
    </a>
);

export default function CartaoPage() {
    const { slug } = useParams();
    const cartao = CARTOES[slug];
    const [qr, setQr] = useState(null);
    const [qrAberto, setQrAberto] = useState(false);
    const url = `${CARTAO_COMUM.websiteUrl}/cartao/${slug}`;

    useSEO({
        title: cartao ? `${cartao.nome} — ${cartao.cargo} | Fados à Mesa` : "Cartão | Fados à Mesa",
        description: "Cartão de visita digital — Associação Fados à Mesa.",
        robots: "noindex, nofollow",
    });

    if (!cartao) return <Navigate to="/" replace />;

    const guardarContacto = () => {
        const blob = new Blob([buildVCard(slug, cartao)], { type: "text/vcard;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `cartao-${slug}.vcf`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const gerarQr = async () => {
        if (!qr) {
            const dataUrl = await QRCode.toDataURL(url, {
                width: 640,
                margin: 2,
                color: { dark: "#070503", light: "#f5efe0" },
            });
            setQr(dataUrl);
        }
        setQrAberto(true);
    };

    return (
        <div className="min-h-screen bg-[#070503] flex items-center justify-center px-5 py-10" data-testid="cartao-page">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-sm border border-[#c9a24b]/35 bg-[#0a0705] px-7 py-9 sm:px-9"
                data-testid="cartao-card"
            >
                <div className="flex flex-col items-center text-center">
                    <img src="/images/logo.webp" alt="Fados à Mesa — 1ª Edição" className="h-12 w-auto" width="1295" height="388" data-testid="cartao-logo" />

                    {cartao.foto ? (
                        <img src={cartao.foto} alt={cartao.nome}
                            className="w-28 h-28 rounded-full object-cover border-2 border-[#c9a24b]/50 mt-8" data-testid="cartao-foto" />
                    ) : (
                        <div className="w-28 h-28 rounded-full border-2 border-[#c9a24b]/50 mt-8 flex items-center justify-center" data-testid="cartao-foto">
                            <span className="font-display text-3xl gold-text">{iniciais(cartao.nome)}</span>
                        </div>
                    )}

                    <h1 className="font-display font-medium text-2xl text-[#efe6d6] mt-5 tracking-wide" data-testid="cartao-nome">
                        {cartao.nome}
                    </h1>
                    <p className="font-caps text-[10px] tracking-[0.3em] uppercase gold-text mt-2.5" data-testid="cartao-cargo">
                        {cartao.cargo}
                    </p>
                    <p className="font-display italic font-light text-xs text-[#efe6d6]/55 mt-3" data-testid="cartao-fundador">
                        {CARTAO_COMUM.fundador}
                    </p>
                </div>

                <div className="flex justify-center my-7">
                    <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                </div>

                <div className="space-y-4">
                    <Linha href={telHref(cartao.telemovel)} icon={Phone} texto={cartao.telemovel} testid="cartao-telefone" />
                    <Linha href={waHref(cartao.telemovel)} icon={MessageCircle} texto="WhatsApp" testid="cartao-whatsapp" externo />
                    <Linha href={`mailto:${CARTAO_COMUM.email}`} icon={Mail} texto={CARTAO_COMUM.email} testid="cartao-email" />
                    <Linha href={CARTAO_COMUM.moradaMaps} icon={MapPin} texto={CARTAO_COMUM.morada} testid="cartao-morada" externo />
                    <Linha href={CARTAO_COMUM.websiteUrl} icon={Globe} texto={CARTAO_COMUM.website} testid="cartao-website" externo />
                </div>

                {CARTAO_COMUM.mostrarSociais && (CARTAO_COMUM.instagram || CARTAO_COMUM.facebook) && (
                    <div className="flex items-center justify-center gap-5 mt-7" data-testid="cartao-sociais">
                        {CARTAO_COMUM.instagram && (
                            <a href={CARTAO_COMUM.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram Fados à Mesa"
                                data-testid="cartao-instagram"
                                className="flex items-center justify-center w-10 h-10 border border-[#c9a24b]/30 text-[#c9a24b] hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-300">
                                <Instagram size={16} strokeWidth={1.6} />
                            </a>
                        )}
                        {CARTAO_COMUM.facebook && (
                            <a href={CARTAO_COMUM.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook Fados à Mesa"
                                data-testid="cartao-facebook"
                                className="flex items-center justify-center w-10 h-10 border border-[#c9a24b]/30 text-[#c9a24b] hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-300">
                                <Facebook size={16} strokeWidth={1.6} />
                            </a>
                        )}
                    </div>
                )}

                <div className="mt-8 space-y-3">
                    <button onClick={guardarContacto} data-testid="cartao-vcard"
                        className="w-full inline-flex items-center justify-center gap-2.5 font-caps text-[11px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-6 py-3.5 hover:bg-[#ecd49b] transition-colors duration-300">
                        <Download size={14} /> Guardar Contacto
                    </button>
                    <button onClick={gerarQr} data-testid="cartao-qr-button"
                        className="w-full inline-flex items-center justify-center gap-2.5 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/50 px-6 py-3.5 hover:bg-[#c9a24b]/10 transition-colors duration-300">
                        <QrCode size={14} /> QR Code do Cartão
                    </button>
                </div>
            </motion.div>

            <AnimatePresence>
                {qrAberto && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-[#070503]/90 backdrop-blur-sm px-5"
                        onClick={() => setQrAberto(false)}
                        data-testid="cartao-qr-overlay"
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="border border-[#c9a24b]/40 bg-[#0a0705] p-7 text-center max-w-xs w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-end">
                                <button onClick={() => setQrAberto(false)} aria-label="Fechar" data-testid="cartao-qr-fechar"
                                    className="text-[#efe6d6]/60 hover:text-[#c9a24b] transition-colors duration-300">
                                    <X size={18} />
                                </button>
                            </div>
                            {qr && <img src={qr} alt={`QR code — ${url}`} className="w-full h-auto mt-2" data-testid="cartao-qr-imagem" />}
                            <p className="font-light text-[11px] text-[#efe6d6]/50 mt-4 break-all">{url}</p>
                            {qr && (
                                <a href={qr} download={`cartao-${slug}-qr.png`} data-testid="cartao-qr-download"
                                    className="mt-5 inline-flex items-center justify-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-6 py-3 hover:bg-[#ecd49b] transition-colors duration-300 w-full">
                                    <Download size={13} /> Descarregar QR
                                </a>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
